// @services/clients/LifecycleClient.ts
import type { LifecycleClient as InteropLifecycleClient } from '@ps-bridge/clients/lifecycle';
import type {
    CreateDocumentEditRequest,
    CreateDocumentExampleRequest,
    CreateDocumentScratchRequest,
    CreateDocumentResponse,
    DocumentFetchResponse,
    DocumentID,
    LifecycleError,
    ListAvailableDocumentsResponse,
    OutlineResponse,
    UploadSourceDocumentRequest,
    UploadSourceDocumentResponse,
} from '@ps-bridge/models/core/dataContracts';
import type { TaskEitherTs } from '@ps-bridge/interop';
import type { Ok } from '@ps-bridge/ok';
import { taskEitherFromRaw } from '@utils/effects/branded';

type CreateDocumentRequest =
    | CreateDocumentScratchRequest
    | CreateDocumentEditRequest
    | CreateDocumentExampleRequest;

export interface LifecycleClient {
    readonly createDocument: (
        request: CreateDocumentRequest,
    ) => TaskEitherTs<LifecycleError, CreateDocumentResponse>;
    readonly uploadSourceDocument: (
        request: UploadSourceDocumentRequest,
    ) => TaskEitherTs<LifecycleError, UploadSourceDocumentResponse>;
    readonly fetchOutline: (documentId: DocumentID) => TaskEitherTs<LifecycleError, OutlineResponse>;
    readonly fetchDocument: (
        documentId: DocumentID,
    ) => TaskEitherTs<LifecycleError, DocumentFetchResponse>;
    readonly listAvailableDocuments: TaskEitherTs<LifecycleError, ListAvailableDocumentsResponse>;
    readonly downloadDocumentAsBlob: (documentId: DocumentID) => TaskEitherTs<LifecycleError, Blob>;
    readonly deleteDocument: (documentId: DocumentID) => TaskEitherTs<LifecycleError, Ok>;
}

export const fromInteropLifecycleClient = (client: InteropLifecycleClient): LifecycleClient => ({
    createDocument: (request) => taskEitherFromRaw(client.createDocument(request)),
    uploadSourceDocument: (request) => taskEitherFromRaw(client.uploadSourceDocument(request)),
    fetchOutline: (documentId) => taskEitherFromRaw(client.fetchOutline(documentId)),
    fetchDocument: (documentId) => taskEitherFromRaw(client.fetchDocument(documentId)),
    listAvailableDocuments: taskEitherFromRaw(client.listAvailableDocuments),
    downloadDocumentAsBlob: (documentId) => taskEitherFromRaw(client.downloadDocumentAsBlob(documentId)),
    deleteDocument: (documentId) => taskEitherFromRaw(client.deleteDocument(documentId)),
});
